import React, { useEffect, useState, useRef, useContext, useMemo } from 'react';
import AuthContext from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { getCookie, setCookie } from '../utils/cookieUtils';
import { canEditContent } from '../utils/permissions';
import { trackNavigationClick } from '../utils/analytics';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    MenuIcon,
    CloseIcon,
    HomeIcon as Home,
    InfoIcon as Info,
    BlogIcon as FileText,
    TeamIcon as Users,
    CalendarIcon as Calendar,
    ChevronDownIcon as ChevronDown,
    CookieIcon as Cookie,
    DashboardIcon as LayoutDashboard,
    DarkModeIcon as Moon,
    LightModeIcon as Sun,
    HeartIcon as Heart,
    MailIcon as Mail,
    ChevronRightIcon as ChevronRight,
    ChevronLeftIcon,
    EditIcon as Edit2
} from '../components/Icons';

const Navbar = () => {
    const { user } = useContext(AuthContext);
    const { organization, settings } = useSettings();

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    // Determine if user can edit the current page and compute the dash edit URL
    const DASH_URL = process.env.REACT_APP_DASHBOARD_URL || 'https://dash.pfadfinder-wutoeschingen.org';
    const editUrl = useMemo(() => {
        if (!user) return null;
        const path = location.pathname;

        // Home page: /
        if ((path === '/' || path === '') && (user.role === 'admin' || user.role === 'gruppenleiter' || canEditContent(user, 'settings'))) {
            return `${DASH_URL}/admin-tools/homepage`;
        }

        // Event detail: /events/:slug
        const eventMatch = path.match(/^\/events\/([^/]+)$/);
        if (eventMatch && canEditContent(user, 'event')) {
            return `${DASH_URL}/edit/events/${eventMatch[1]}`;
        }

        // Blog detail: /blog/:id
        const blogMatch = path.match(/^\/blog\/([^/]+)$/);
        if (blogMatch && canEditContent(user, 'blog')) {
            return `${DASH_URL}/edit/blog/${blogMatch[1]}`;
        }

        // Team member: /t/:id
        const teamMatch = path.match(/^\/t\/([^/]+)$/);
        if (teamMatch && canEditContent(user, 'team')) {
            return `${DASH_URL}/edit/t/${teamMatch[1]}`;
        }

        return null;
    }, [user, location.pathname, DASH_URL]);

    // Dark Mode Logic
    const [isDarkMode, setIsDarkMode] = useState(() => {
        if (typeof window !== 'undefined') {
            const savedMode = getCookie('theme_mode');
            if (savedMode) {
                return savedMode === 'dark';
            }
            return window.matchMedia('(prefers-color-scheme: dark)').matches;
        }
        return false;
    });

    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add('dark');
            document.documentElement.classList.remove('light');
            setCookie('theme_mode', 'dark');
        } else {
            document.documentElement.classList.remove('dark');
            document.documentElement.classList.add('light');
            setCookie('theme_mode', 'light');
        }
    }, [isDarkMode]);

    const toggleTheme = () => {
        setIsDarkMode((prev) => !prev);
    };

    const enabledSections = useMemo(() => {
        const modules = settings?.modules || {};
        if (!settings?.sectionsOrder || !Array.isArray(settings.sectionsOrder)) {
            return {
                intro: true,
                about: true,
                groups: modules.groups !== false,
                team: modules.team !== false,
                blog: modules.blog !== false,
                events: modules.events !== false,
                contact: true
            };
        }
        const map = {};
        settings.sectionsOrder.forEach(s => {
            if (s && s.id) map[s.id] = s.enabled !== false;
        });
        if (modules.groups === false) map.groups = false;
        if (modules.team === false) map.team = false;
        if (modules.blog === false) map.blog = false;
        if (modules.events === false) map.events = false;
        return map;
    }, [settings?.sectionsOrder, settings?.modules]);

    const navLinks = useMemo(() => {
        const all = [
            { id: 'hero', path: '/#hero-section', label: 'Startseite', icon: Home, enabled: true },
            { id: 'about', path: '/#about-section', label: 'Über Uns', icon: Info, enabled: enabledSections.about !== false },
            { id: 'groups', path: '/#groups-section', label: 'Gruppen', icon: Users, enabled: enabledSections.groups !== false },
            { id: 'team', path: '/#team-section', label: 'Team', icon: Users, enabled: enabledSections.team !== false },
            { id: 'blog', path: '/#blog-section', label: 'Blog', icon: FileText, enabled: enabledSections.blog !== false },
            { id: 'events', path: '/#events-section', label: 'Events', icon: Calendar, enabled: enabledSections.events !== false },
            { id: 'contact', path: '/#contact-section', label: 'Kontakt', icon: Mail, enabled: enabledSections.contact !== false },
            { path: process.env.REACT_APP_DASHBOARD_URL || 'https://dash.pfadfinder-wutoeschingen.org', label: 'Dashboard', icon: LayoutDashboard, external: true, enabled: true },
        ];
        return all.filter(l => l.enabled !== false);
    }, [enabledSections]);

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location]);


    const handleSectionClick = (id, label) => {
        setIsMobileMenuOpen(false);
        trackNavigationClick(label || id, `/#${id}`);
        if (location.pathname !== '/') {
            navigate(`/#${id}`);
        } else {
            const element = document.getElementById(id);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    const openCookieSettings = () => {
        if (window.PF_OpenCookieSettings) {
            window.PF_OpenCookieSettings();
        } else {
            window.dispatchEvent(new Event('openCookieSettings'));
        }
        setIsMobileMenuOpen(false);
    };


    return (
        <>
            <nav className="sticky top-6 z-[100] mx-auto my-8 w-[92%] max-w-7xl bg-surface-containerHighest/95 backdrop-blur-xl border border-outlineVariant/50 rounded-full shadow-elevation-3 transition-all duration-medium2">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16 md:h-20">
                        {/* Brand or Back Buttons */}
                        {location.pathname === '/' ? (
                            <Link
                                to="/"
                                className="flex items-center gap-3 group"
                            >
                                <span className="text-xl font-bold tracking-tight text-surface-on">
                                    {(organization?.stammName || 'Pfadfinder Wutöschingen').split(' ')[0]}{' '}
                                    <span className="text-primary">
                                        {(organization?.stammName || 'Pfadfinder Wutöschingen').split(' ').slice(1).join(' ')}
                                    </span>
                                </span>
                            </Link>
                        ) : (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => navigate(-1)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-surface-on/5 hover:bg-surface-on/10 text-surface-on font-bold transition-all"
                                >
                                    <ChevronLeftIcon style={{ fontSize: 20 }} /> Zurück
                                </button>
                                <Link
                                    to="/"
                                    className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-surface-on/5 hover:bg-surface-on/10 text-surface-on font-bold transition-all"
                                >
                                    Home
                                </Link>
                            </div>
                        )}

                        {/* Desktop Navigation */}
                        <div className="hidden md:flex items-center gap-2">

                            {navLinks.map((link) => {
                                const isAnchor = link.path.startsWith('/#');
                                const sectionId = isAnchor ? link.path.substring(2) : null;
                                
                                return link.external ? (
                                    <a
                                        key={link.path}
                                        href={link.path}
                                        onClick={() => trackNavigationClick(link.label, link.path)}
                                        className="px-4 py-2.5 rounded-full text-sm font-bold transition-all text-surface-onVariant hover:bg-surface-on/5 whitespace-nowrap"
                                    >
                                        {link.label}
                                    </a>
                                ) : isAnchor ? (
                                    <button
                                        key={link.path}
                                        onClick={() => handleSectionClick(sectionId, link.label)}
                                        className={`px-4 py-2.5 rounded-full text-sm font-bold transition-all whitespace-nowrap ${
                                            (location.pathname === '/' && location.hash === `#${sectionId}`) || (location.pathname === '/' && sectionId === 'hero-section' && !location.hash)
                                            ? 'bg-primary/10 text-primary'
                                            : 'text-surface-onVariant hover:bg-surface-on/5'
                                        }`}
                                    >
                                        {link.label}
                                    </button>
                                ) : (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        onClick={() => trackNavigationClick(link.label, link.path)}
                                        className={`px-4 py-2.5 rounded-full text-sm font-bold transition-all whitespace-nowrap ${location.pathname.startsWith(link.path)
                                            ? 'bg-secondary-container text-secondary-onContainer'
                                            : 'text-surface-onVariant hover:bg-surface-on/5'
                                            }`}
                                    >
                                        {link.label}
                                    </Link>
                                );
                            })}

                            <div className="w-px h-6 bg-outlineVariant mx-2 opacity-50"></div>

                            {/* Glowing Edit Pen Button */}
                            {editUrl && (
                                <a
                                    href={editUrl}
                                    className="p-2.5 rounded-full bg-primary text-primary-on edit-pen-glow"
                                    title="Diese Seite bearbeiten"
                                    aria-label="Diese Seite im Dashboard bearbeiten"
                                >
                                    <Edit2 style={{ fontSize: 20 }} />
                                </a>
                            )}
                            <button
                                onClick={openCookieSettings}
                                className="p-2.5 rounded-full text-surface-on hover:bg-surface-on/5 transition-all"
                                title="Cookie Einstellungen"
                                aria-label="Cookie Einstellungen anpassen"
                            >
                                <Cookie style={{ fontSize: 20 }} className="text-primary" />
                            </button>

                            <button
                                onClick={toggleTheme}
                                className={`p-2.5 rounded-full transition-all duration-emphasized ${isDarkMode ? 'bg-primary-container text-primary-onContainer' : 'bg-surface-containerHighest text-surface-onVariant'
                                    }`}
                                title={isDarkMode ? "Licht an" : "Licht aus"}
                                aria-label={isDarkMode ? "Hellen Modus aktivieren" : "Dunklen Modus aktivieren"}
                            >
                                {isDarkMode ? <Sun style={{ fontSize: 20 }} className="animate-pulse" /> : <Moon style={{ fontSize: 20 }} />}
                            </button>
                        </div>

                        {/* Mobile Toggle */}
                        <div className="flex md:hidden items-center gap-2">
                            <button
                                onClick={toggleTheme}
                                aria-label={isDarkMode ? "Hellen Modus aktivieren" : "Dunklen Modus aktivieren"}
                                className={`p-2.5 rounded-full ${isDarkMode ? 'bg-primary-container text-primary-onContainer' : 'bg-surface-containerLow text-surface-onVariant'}`}
                            >
                                {isDarkMode ? <Sun style={{ fontSize: 20 }} /> : <Moon style={{ fontSize: 20 }} />}
                            </button>
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                aria-label={isMobileMenuOpen ? "Menü schließen" : "Hauptmenü öffnen"}
                                className="p-3 rounded-2xl bg-surface-containerLow text-surface-on hover:bg-surface-on/5 transition-all"
                            >
                                {isMobileMenuOpen ? <CloseIcon style={{ fontSize: 24 }} /> : <MenuIcon style={{ fontSize: 24 }} />}
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile Drawer via Portal to escape stacking context */}
            {isMobileMenuOpen && typeof document !== 'undefined' && document.body && createPortal(
                <div className="fixed inset-0 z-[99999] md:hidden">
                    {/* Overlay */}
                    <div
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fade-in"
                        onClick={() => setIsMobileMenuOpen(false)}
                    />

                    {/* Content */}
                    <div className="absolute right-0 top-0 h-full w-[85%] max-w-[400px] bg-surface-containerLowest shadow-elevation-5 animate-slide-in-right overflow-hidden flex flex-col rounded-l-[2.5rem]">
                        <div className="p-6 flex items-center justify-between border-b border-outlineVariant/30 shrink-0">
                            <span className="text-xl font-black text-primary">Pfadfinder WT</span>
                            <button onClick={() => setIsMobileMenuOpen(false)} aria-label="Menü schließen" className="p-2 rounded-full hover:bg-surface-on/5 text-surface-onVariant">
                                <CloseIcon style={{ fontSize: 24 }} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-2">
                            {navLinks.map((link) => {
                                const Icon = link.icon;
                                const isAnchor = link.path.startsWith('/#');
                                const sectionId = isAnchor ? link.path.substring(2) : null;

                                if (link.external) {
                                    return (
                                        <a
                                            key={link.path}
                                            href={link.path}
                                            onClick={() => { setIsMobileMenuOpen(false); trackNavigationClick(link.label, link.path); }}
                                            className="flex items-center gap-4 p-4 rounded-2xl font-bold transition-all text-surface-onVariant hover:bg-surface-on/5"
                                        >
                                            <Icon style={{ fontSize: 22 }} className="opacity-60" />
                                            {link.label}
                                            <ChevronRight style={{ fontSize: 18 }} className="ml-auto opacity-30" />
                                        </a>
                                    );
                                }

                                if (isAnchor) {
                                    return (
                                        <button
                                            key={link.path}
                                            onClick={() => handleSectionClick(sectionId, link.label)}
                                            className={`w-full flex items-center gap-4 p-4 rounded-2xl font-bold transition-all ${
                                                (location.pathname === '/' && location.hash === `#${sectionId}`) || (location.pathname === '/' && sectionId === 'hero-section' && !location.hash)
                                                ? 'bg-primary/10 text-primary'
                                                : 'text-surface-onVariant hover:bg-surface-on/5'
                                            }`}
                                        >
                                            <Icon style={{ fontSize: 22 }} className={(location.pathname === '/' && location.hash === `#${sectionId}`) ? 'text-primary' : 'opacity-60'} />
                                            {link.label}
                                            <ChevronRight style={{ fontSize: 18 }} className="ml-auto opacity-30" />
                                        </button>
                                    );
                                }

                                return (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        onClick={() => { setIsMobileMenuOpen(false); trackNavigationClick(link.label, link.path); }}
                                        className={`flex items-center gap-4 p-4 rounded-2xl font-bold transition-all ${location.pathname.startsWith(link.path)
                                            ? 'bg-secondary/10 text-secondary'
                                            : 'text-surface-onVariant hover:bg-surface-on/5'
                                            }`}
                                    >
                                        <Icon style={{ fontSize: 22 }} className={location.pathname.startsWith(link.path) ? 'text-secondary' : 'opacity-60'} />
                                        {link.label}
                                        <ChevronRight style={{ fontSize: 18 }} className="ml-auto opacity-30" />
                                    </Link>
                                );
                            })}
                        </div>


                        <div className="p-6 space-y-2">
                            {/* Mobile Edit Pen Button */}
                            {editUrl && (
                                <a
                                    href={editUrl}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="w-full flex items-center gap-4 p-4 rounded-2xl bg-primary text-primary-on font-bold edit-pen-glow transition-all"
                                >
                                    <Edit2 style={{ fontSize: 22 }} />
                                    Diese Seite bearbeiten
                                </a>
                            )}
                        </div>
                        <div className="p-6 bg-surface-containerLow border-t border-outlineVariant/30 shrink-0">
                            <button
                                onClick={openCookieSettings}
                                className="w-full flex items-center gap-4 p-4 rounded-2xl text-surface-on font-bold hover:bg-surface-on/5 transition-all"
                            >
                                <Cookie style={{ fontSize: 22 }} className="text-primary" />
                                Cookie Einstellungen
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default Navbar;